<script lang="ts">
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import FaqItems from "@components/FaqItems.svelte";
	import FoldingCards from "@components/FoldingCards.svelte";
	import InfoCard from "@components/InfoCard.svelte";
	import Ol from "@components/Ol.svelte";
	import { OlSize } from "@components/OlProps.js";
	import ReferenceContents from "@components/ReferenceContents.svelte";
	import Section from "@components/Section.svelte";
	import ServicesCards from "@components/ServicesCards.svelte";
	import TechnologyCards from "@components/TechnologyCards.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import { getClientsPath } from "@core/schema/paths/getClientsPath.js";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import { getFaqsRescueProject } from "../data/getFaqsRescueProject.js";
	import { getReasonsForCoop } from "../data/getReasonsForCoop.js";
	import { getReasonsToRescueProject } from "../data/getReasonsToRescueProject.js";
	import { ReferencedClients } from "../data/getReferences.js";
	import { getRescueWhyFastest } from "../data/getRescueWhyFastest.js";
	import { getServicesRescue } from "../data/getServicesRescue.js";
	import { getTechnologies } from "../data/getTechnologies.js";
	import { getWhenRescueProject } from "../data/getWhenRescueProject.js";
	import type { ProjectRescueProps } from "./ProjectRescueProps.js";

	const { tenant } = getGlobalTemplateProps();
	const _: ProjectRescueProps = $props();
	const rescueProcess = [
		{ title: "Úvodní analýza a diagnostika" },
		{ title: "Návrh řešení a přesného postupu" },
		{ title: "Převzetí vývoje" },
		{ title: "Dokončení projektu" },
		{ title: "Podpora po dokončení" },
	];
</script>

<Layout withStructuredData>
	<Section background={Background.LightGradient} centered gridHeadline="container-6" gridDesc="container-6" hero>
		{#snippet headline()}
			<Formatted text="Hodíme vašemu projektu cg[[záchranné lano]]" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="co[[wm[[Máte ve firmě rozdělaný (nejen) IT projekt a nevíte, co s ním dál?]]]] nl[[]] Pomůžeme vám ho zachránit nebo dokončit."
				/>
			</p>
		{/snippet}
		{#snippet cta()}
			<Link
				variant={ButtonVariant.PhloxOutlineLightBg}
				class="flex items-center justify-center mx-auto mt-2"
				href={getConnectWithFastestPath(tenant)}
			>
				<Formatted text="Potřebujeme vaši pomoc" />
				<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
			</Link>
		{/snippet}
	</Section>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="cg[[Kdy službu]] záchrany či převzetí projektu cg[[využít]]?" />
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<Ol items={getWhenRescueProject(tenant)} size={OlSize.Medium} />
			</div>
		{/snippet}
	</Section>

	<Section
		background={Background.LightGradient}
		noPadding="bot"
		gridHeadline="container-8 lg:text-center"
		gridDesc="container-8"
		centered
	>
		{#snippet headline()}
			<Formatted text="S čím cg[[za námi]] zákazníci často chodí" />
		{/snippet}
	</Section>
	<Section gridChildren="max-md:full-width" background={Background.White} noPadding="both">
		<div
			class="grid grid-cols-1 md:grid-cols-3 gap-1 max-lg:full-width lg:px-1 bg-fstlight-100 border-t-4 border-fstlight-100"
		>
			{#each getReasonsToRescueProject(tenant) as { title, desc }, index (index)}
				<InfoCard {title} {desc} />
			{/each}
		</div>
	</Section>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="Jak vypadá cg[[proces]] převzetí projektu" />
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<Ol items={rescueProcess} size={OlSize.Medium} />
			</div>
		{/snippet}
	</Section>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="cg[[5 důvodů]], proč využít pomoci od Fastest" />
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<Ol items={getRescueWhyFastest(tenant)} size={OlSize.Medium} />
			</div>
		{/snippet}
	</Section>

	<Section centered gridHeadline="container-6" noPadding="bot">
		{#snippet headline()}
			<Formatted text="Další služby a řešení, se kterými vám budeme cg[[k ruce]]" />
		{/snippet}
	</Section>
	<ServicesCards cards={getServicesRescue(tenant)} />

	<Section id="reference" gridHeadline="container-6" centered>
		{#snippet headline()}
			<Formatted text="Ukázky cg[[záchrany projektů]] pro naše klienty" />
		{/snippet}
		<div class="mt-12">
			<ReferenceContents
				clients={[ReferencedClients.Horse, ReferencedClients.OxPoint, ReferencedClients.Tymbe]}
				descriptionKey="descRescue"
			/>
		</div>
		<Link variant={ButtonVariant.Phlox} class="mx-auto flex items-center justify-center" href={getClientsPath(tenant)}
			>Další ukázky práce</Link
		>
	</Section>

	<Section
		gridHeadline="lg:container-start-2 lg:container-end-5"
		gridChildren="lg:container-start-7 lg:container-end-11"
		background={Background.LightGradient}
		paddingFollowNext
		class="items-end"
	>
		{#snippet headline()}
			<Formatted text="Používáme cg[[moderní]] a cg[[ověřené]] technologie" />
		{/snippet}
		<p>
			<Formatted
				text="Pracujeme s moderními a dlouhodobě podporovanými technologiemi, které nabízí ideální poměr cena/výkon. Vyvíjíme primárně na cloudu AWS (Amazon Web Services), což znamená, že vám ušetříme až desítky tisíc Kč za dedikované servery."
			/>
		</p>
	</Section>
	<TechnologyCards technologies={getTechnologies(tenant)} />
	<Link
		variant={ButtonVariant.Phlox}
		class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
		href={getConnectWithFastestPath(tenant)}
		>Chci fastest služby
		<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
	</Link>

	<Section centered gridHeadline="container-6">
		{#snippet headline()}
			<Formatted text="Časté dotazy k cg[[záchraně a převzetí projektu]]" />
		{/snippet}
		<FaqItems faqs={getFaqsRescueProject(tenant)} />
	</Section>

	<Section
		background={Background.Onyx}
		gridHeadline="container-6 lg:container-start-1 lg:container-end-5 xl:container-start-2 xl:container-end-4 max-lg:text-center"
		gridDesc="lg:container-start-7 lg:container-end-12 xl:container-start-6 xl:container-end-11"
	>
		{#snippet headline()}
			<Formatted text="cg[[Proč]] si plácnout s cg[[Fastest Solution]]" />
		{/snippet}
		{#snippet desc()}
			<FoldingCards color={Background.Onyx} cards={getReasonsForCoop(tenant)} />
		{/snippet}
		<div class="container-12">
			<Link
				variant={ButtonVariant.Phlox}
				class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
				href={getConnectWithFastestPath(tenant)}
				>Chci fastest služby
				<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
			</Link>
		</div>
	</Section>

	{@const contacts = getBookMeetingPeople(tenant)}
	<BookMeeting contacts={[contacts[BookMeetingPerson.Kucera]]} />
</Layout>
